import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import PendingApprovalPage from "./pages/PendingApprovalPage";
import UpdateAvailableToast from "./components/UpdateAvailableToast";
import NotificationProvider from "./notifications/NotificationProvider";
import NotificationToaster from "./notifications/NotificationToaster";
import SharedDashboardPage from "./components/home/SharedDashboardPage";
import OnboardingForm from "./pages/OnboardingForm";
import RouteErrorBoundary from "./components/RouteErrorBoundary";

/* ── Performance-Mode auto-detection ─────────────────────────────
 * Some devices — notably Galaxy S22-S26 Ultra on Samsung Internet
 * and stock Chrome — cannot keep up with the app's backdrop-filter
 * + mix-blend-mode + heavy decorative blurs.  Symptoms: layers
 * ghost-render (the user's bingo screenshot showed two stacked
 * boards), menus flash on every paint, taps fall through to the
 * wrong layer.  Setting `body.perf-mode` swaps the GPU-heavy
 * effects for cheap solid surfaces while preserving every layout,
 * color and z-index — so functionality is identical.
 *
 * Triggered by ANY of:
 *   1. `localStorage.cs_perf_mode === '1'` (user toggle in Settings)
 *   2. User agent looks like Samsung Internet
 *   3. `prefers-reduced-transparency: reduce` is set by the OS
 */
function applyPerfMode() {
  try {
    const ls = localStorage.getItem("cs_perf_mode");
    const ua = (navigator.userAgent || "").toLowerCase();
    const samsung = ua.includes("samsungbrowser");
    const reduced = window.matchMedia &&
      window.matchMedia("(prefers-reduced-transparency: reduce)").matches;
    const on = ls === "1" || (ls !== "0" && (samsung || reduced));
    document.body.classList.toggle("perf-mode", !!on);
  } catch { /* SSR / locked-down storage — ignore */ }
}
// Run once before React mounts so the very first paint is correct.
applyPerfMode();
// Listen for in-app toggles (Settings dispatches this event).
window.addEventListener("cs:perf-mode-changed", applyPerfMode);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#CD040B] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.status === "pending") {
    return <PendingApprovalPage />;
  }

  return children;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Detect session_id synchronously during render (before ProtectedRoute checks auth)
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id from Google OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/shared/:token" element={<SharedDashboardPage />} />
      <Route path="/onboarding" element={<OnboardingForm />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  // Handle SW notificationclick messages → navigate the active tab.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMsg = (e) => {
      if (e?.data?.type === 'navigate' && typeof e.data.url === 'string') {
        try { window.location.href = e.data.url; } catch (_) { /* ignore */ }
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, []);

  return (
    <RouteErrorBoundary routeLabel="the app">
      <AuthProvider>
        <BrowserRouter>
          <NotificationProvider>
            <AppRouter />
            <NotificationToaster />
          </NotificationProvider>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <UpdateAvailableToast />
      </AuthProvider>
    </RouteErrorBoundary>
  );
}

export default App;
